import { Button } from '@/global/components/Button'
import type { Combobox } from '@/global/components/Combobox'
import Menu from '@/global/components/Menu'
import { cn } from '@/lib/utils'
import { CheckIcon } from 'lucide-react'
import React from 'react'
import { useNavigate } from 'react-router-dom'

type ButtonProps = Omit<React.ComponentProps<typeof Button>, 'href'>

interface IPropsMenuButton extends ButtonProps {
    children?: React.ReactNode
    href?: string
    startIcon: JSX.Element
    label?: string
    position?:
        | 'left'
        | 'right'
        | 'center'
        | 'top-left'
        | 'top-right'
        | 'top-center'
}

export const GridMenuButton = React.forwardRef<
    HTMLButtonElement,
    IPropsMenuButton
>(({ children, startIcon, label, ...rest }, ref) => {
    return (
        <Menu.Root id={rest.id} position={rest.position} arrow>
            <Menu.Trigger>
                <GridButton ref={ref} {...{ startIcon, label, ...rest }} />
            </Menu.Trigger>
            <Menu.List>{children}</Menu.List>
        </Menu.Root>
    )
})

GridMenuButton.displayName = 'GridMenuButton'

interface IGridButtonProps extends ButtonProps {
    href?: string
    onlyView?: number | number[]
    startIcon: JSX.Element
    label?: string
}

export const GridButton = React.forwardRef<HTMLButtonElement, IGridButtonProps>(
    ({ label, href, onClick, ...props }, ref) => {
        const navigate = useNavigate()

        const Icon = props.startIcon && !label ? props.startIcon : null
        const startIcon = props.startIcon && label ? props.startIcon : null

        return (
            <Button
                {...props}
                ref={ref}
                startIcon={startIcon}
                variant="text"
                className={cn(
                    'flex flex-row items-center',
                    'justify-start md:justify-center md:w-fit w-full',
                    'h-10 text-xs',
                    'hover:bg-primary/15',
                    props.className,
                )}
                onClick={e => {
                    if (href) {
                        e.preventDefault()
                        onClick?.(e)
                        navigate(href)
                    } else if (onClick) {
                        onClick(e)
                    }
                }}
            >
                {Icon}
                {!!label && <span>{label}</span>}
                {props.children}
            </Button>
        )
    },
)

GridButton.displayName = 'GridButton'

export function GridMenuItem({
    label,
    startIcon = <React.Fragment />,
    ...props
}: React.ComponentProps<typeof Menu.Item> & {
    startIcon?: JSX.Element
    label: string
}) {
    return (
        <Menu.Item {...props}>
            {React.cloneElement(startIcon, {
                className: cn('mr-1', startIcon.props.className),
            })}
            <span>{label}</span>
            {props.children}
        </Menu.Item>
    )
}

interface MultipleOption {
    value: string
    label: string
}

interface IPropsMenuButtonMultiple {
    startIcon?: React.ReactNode
    label: string
    options: MultipleOption[]
    selected: Set<string>
    onChange: (selected: Set<string>) => void
}

/**
 * Versão múltipla do {@link GridMenuButton}: em vez de itens de ação, uma lista de
 * opções que ficam marcadas. A lista é a do {@link Combobox} múltiplo — linha com
 * marca de seleção, sem caixa de marcar.
 */
export function GridMenuButtonMultiple({
    startIcon,
    label,
    options,
    selected,
    onChange,
}: IPropsMenuButtonMultiple) {
    const alternar = (value: string) => {
        const proximo = new Set(selected)
        proximo.has(value) ? proximo.delete(value) : proximo.add(value)
        onChange(proximo)
    }

    return (
        <Menu.Root arrow>
            <Menu.Trigger>
                <GridButton
                    startIcon={startIcon as JSX.Element}
                    label={label}
                />
            </Menu.Trigger>
            <Menu.List>
                {options.map(option => (
                    <Menu.Item
                        key={option.value}
                        onSelect={event => {
                            // Sem o preventDefault o Radix fecha o menu
                            event.preventDefault()
                            alternar(option.value)
                        }}
                        className="gap-2"
                        onClick={e => e.stopPropagation()}
                    >
                        <span className="flex-1 truncate">{option.label}</span>
                        {selected.has(option.value) ? (
                            <CheckIcon className="size-5 text-current" />
                        ) : (
                            <div className="size-5" />
                        )}
                    </Menu.Item>
                ))}
            </Menu.List>
        </Menu.Root>
    )
}
